import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { LeadFlowAnimation } from '../components/LeadFlowAnimation';
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-572">
      <SEOHead title="Viridia Analytics | Phoenix Marketing Agency for Local Service Businesses" description="Phoenix's #1 growth partner for local service businesses. Expert ad campaigns on Google, Meta & Yelp, smart automations, CRM follow-up, and web design that turns clicks into booked jobs." canonical="/" keywords="Phoenix marketing agency, local business marketing, Google Ads Phoenix, Yelp Ads partner, Meta Ads, marketing automation, CRM, web design Phoenix, SEO Phoenix, lead generation" jsonLd={LOCAL_BUSINESS_SCHEMA} data-id="element-573" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-574">
        {/* Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-575">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-viridia/20 blur-[120px] animate-pulse-slow" data-id="element-576"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia-dark/20 blur-[150px] animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-577"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-578">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-579">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-580">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-581">
                <span className="w-2 h-2 rounded-full bg-viridia animate-pulse" data-id="element-582"></span>
                Phoenix's #1 Growth Partner for Local Businesses
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-583">
                Turn Your Ads Into{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-viridia to-emerald-300" data-id="element-584">
                  Booked Jobs
                </span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg sm:text-xl text-gray-400 mb-10 leading-relaxed" data-id="element-585">
                We help home service businesses in Phoenix drive revenue with
                expert advertising, smart automations, and CRM-driven follow-up
                — so no lead slips through the cracks.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row gap-4" data-id="element-586">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-1" data-id="element-587">
                  Book a Strategy Call
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-588" />
                </Link>
                <a href="#services" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-transparent border border-gray-700 rounded-lg hover:bg-viridia-surface hover:border-gray-600 transition-all duration-300" data-id="element-589">
                  See Our Services
                </a>
              </motion.div>
            </div>

            {/* Hero Visual — Lead Flow Animation */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="relative hidden lg:flex items-center justify-center h-[500px]" data-id="element-590">
              <LeadFlowAnimation data-id="element-591" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY BAR */}
      <section className="bg-viridia-light py-10 border-y border-gray-200" data-id="element-592">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-593">
          <ScrollReveal data-id="element-594">
            <p className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-595">
              Trusted by Phoenix service businesses
            </p>
            <div className="flex overflow-x-auto pb-4 md:pb-0 md:grid md:grid-cols-7 gap-8 items-center justify-between opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-596">
              {['HVAC', 'Plumbing', 'Landscaping', 'Med Spas', 'Law Firms', 'Restaurants', 'Accountants'].map((industry, i) => <div key={i} className="flex-shrink-0 flex items-center justify-center min-w-[120px] md:min-w-0" data-id="element-597">
                  <span className="text-xl font-bold text-gray-800" data-id="element-598">
                    {industry}
                  </span>
                </div>)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-viridia-black relative" data-id="element-599">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-600">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-601">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-602">
              How It Works
            </h2>
            <p className="text-xl text-gray-400" data-id="element-603">
              From first click to booked job — here's the system.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative" data-id="element-604">
            {/* Connecting Line for Desktop */}
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-605"></div>

            {[{
            step: '01',
            title: 'We Audit Your Marketing',
            desc: 'Free strategy call to review your current ads, website, and follow-up process.',
            icon: <SearchIcon className="w-6 h-6 text-viridia" data-id="element-606" />
          }, {
            step: '02',
            title: 'Launch Targeted Campaigns',
            desc: 'We build and manage ads on Google, Meta, and Yelp that reach your ideal customers.',
            icon: <TargetIcon className="w-6 h-6 text-viridia" data-id="element-607" />
          }, {
            step: '03',
            title: 'Automate Your Follow-Up',
            desc: 'Missed calls get texted back. Leads get nurtured. Bookings happen automatically.',
            icon: <MessageSquareIcon className="w-6 h-6 text-viridia" data-id="element-608" />
          }, {
            step: '04',
            title: 'Watch Your Revenue Grow',
            desc: 'Track every lead, every job, every dollar with clear reporting and monthly strategy calls.',
            icon: <TrendingUpIcon className="w-6 h-6 text-viridia" data-id="element-609" />
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-610">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-viridia/50 transition-colors duration-300 group" data-id="element-611">
                  <div className="w-16 h-16 rounded-full bg-viridia-black border-2 border-viridia flex items-center justify-center mb-6 shadow-[0_0_15px_rgba(16,185,129,0.2)] group-hover:scale-110 transition-transform duration-300" data-id="element-612">
                    {item.icon}
                  </div>
                  <div className="text-viridia font-bold text-sm mb-2" data-id="element-613">
                    STEP {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-614">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-615">{item.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section id="services" className="py-24 bg-viridia-light" data-id="element-616">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-617">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-618">
            <h2 className="text-3xl md:text-5xl font-bold text-viridia-black mb-4" data-id="element-619">
              Everything You Need to Grow
            </h2>
            <p className="text-xl text-gray-600" data-id="element-620">
              Stop juggling multiple agencies. We handle the entire customer
              journey.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-621">
            {[{
            title: 'Ad Campaigns',
            desc: 'Expert ad management across Google, Meta, and Yelp. We turn your ad spend into qualified leads.',
            icon: <TargetIcon className="w-8 h-8 text-viridia" data-id="element-622" />,
            link: '/ad-campaigns'
          }, {
            title: 'Automations',
            desc: 'Smart systems that respond to leads, book jobs, and follow up — so you never miss an opportunity.',
            icon: <ZapIcon className="w-8 h-8 text-viridia" data-id="element-623" />,
            link: '/automations'
          }, {
            title: 'Web Design & SEO',
            desc: 'Fast, conversion-focused websites with local SEO that turns searches into customers.',
            icon: <GlobeIcon className="w-8 h-8 text-viridia" data-id="element-624" />,
            link: '/web-design-seo'
          }].map((service, index) => <ScrollReveal key={index} delay={index * 0.15} data-id="element-625">
                <Link to={service.link} className="block h-full" data-id="element-626">
                  <div className="bg-viridia-surface rounded-2xl p-8 h-full border-t-4 border-transparent hover:border-viridia transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group" data-id="element-627">
                    <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center mb-6 group-hover:bg-viridia/20 transition-colors" data-id="element-628">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4" data-id="element-629">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-8 leading-relaxed" data-id="element-630">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-viridia font-medium group-hover:translate-x-2 transition-transform" data-id="element-631">
                      Learn More <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-632" />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 overflow-hidden" data-id="element-633">
        <div className="absolute inset-0 bg-gradient-to-br from-viridia-dark to-viridia-black z-0" data-id="element-634"></div>
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2)_0,transparent_100%)] z-0" data-id="element-635"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-636">
          <ScrollReveal data-id="element-637">
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-638">
              Ready to Stop Losing Leads?
            </h2>
            <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto" data-id="element-639">
              Book a free strategy call and we'll show you exactly where you're
              leaving money on the table.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:-translate-y-1 mb-6" data-id="element-640">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-641" />
            </Link>
            <p className="text-emerald-200/80 font-medium" data-id="element-642">
              No contracts. No obligation. Just results.
            </p>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}